import {
  Box,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  TextField,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import * as React from "react";

import { RobotoButton } from "@/shared/components/RobotoButton";
import { RobotoTypography } from "@/shared/components/RobotoTypography";
import {
  FEEDBACK_CATEGORY_LABELS,
  FeedbackCategory,
  FeedbackSentiment,
  NEGATIVE_CATEGORIES,
  POSITIVE_CATEGORIES,
  type UserFeedbackRecord,
} from "@/shared/domain/agentThread";
import { useSubmitAgentThreadFeedback } from "@/shared/domain/agentThread/hooks";
import { ErrorMonitoringService } from "@/shared/services/ErrorMonitoringService";

import styles from "./AgentThreadFeedback.module.css";

interface FeedbackWizardDialogProps {
  /** Which sentiment the user chose. ``null`` closes the dialog. */
  sentiment: FeedbackSentiment | null;
  threadId: string;
  agentThreadOrgId: string;
  messageSequenceNum: number;
  /** If present and sentiment matches, pre-fill the form to amend it. */
  priorRating: UserFeedbackRecord | undefined;
  onClose: () => void;
}

function categoriesForSentiment(
  sentiment: FeedbackSentiment | null,
): FeedbackCategory[] {
  if (sentiment === FeedbackSentiment.POSITIVE) {
    return [...POSITIVE_CATEGORIES, FeedbackCategory.OTHER];
  }
  if (sentiment === FeedbackSentiment.NEGATIVE) {
    return [...NEGATIVE_CATEGORIES, FeedbackCategory.OTHER];
  }
  return [];
}

/** Coerce a SDK ``FeedbackSentiment`` value into the analytics shape's
 *  ``"positive" | "negative" | null`` literal. Pulled out of the props
 *  literal because nesting the ternary inline breaks no-nested-ternary. */
function sentimentForAnalytics(
  sentiment: FeedbackSentiment | null,
): "positive" | "negative" | null {
  if (sentiment === FeedbackSentiment.POSITIVE) {
    return "positive";
  }
  if (sentiment === FeedbackSentiment.NEGATIVE) {
    return "negative";
  }
  return null;
}

/**
 * Chip-and-notes form for rating a single assistant message. The parent
 * controls open/close via ``sentiment``; ``null`` closes the dialog. When the
 * dialog opens and ``priorRating`` matches the chosen sentiment, the form is
 * pre-filled so amending is low-friction.
 */
export const FeedbackWizardDialog: React.FC<FeedbackWizardDialogProps> = ({
  sentiment,
  threadId,
  agentThreadOrgId,
  messageSequenceNum,
  priorRating,
  onClose,
}) => {
  const theme = useTheme();
  const submitFeedback = useSubmitAgentThreadFeedback();

  const [selectedCategories, setSelectedCategories] = React.useState<
    Set<FeedbackCategory>
  >(new Set());
  const [notes, setNotes] = React.useState("");

  // Re-initialize form state whenever the dialog opens (sentiment flips
  // non-null) or the user switches between positive/negative without closing.
  // Keyed on ``priorRating?.feedback_id`` rather than the whole object —
  // a TanStack refetch returning a structurally-different (but semantically
  // identical) object would otherwise rerun this effect mid-dialog and
  // clobber whatever the user has typed since opening it.
  React.useEffect(() => {
    if (sentiment === null) {
      return;
    }
    if (priorRating && priorRating.sentiment === sentiment) {
      setSelectedCategories(new Set(priorRating.categories));
      setNotes(priorRating.notes ?? "");
    } else {
      setSelectedCategories(new Set());
      setNotes("");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps  -- intentional; see comment above
  }, [sentiment, priorRating?.feedback_id]);

  // Inline error surfaced when the mutation fails. We do NOT close the
  // dialog on error (the previous code did) so the user's typed-in input is
  // preserved and they can try again — the toast/error stays visible until
  // they retry or cancel.
  const [submitError, setSubmitError] = React.useState<string | null>(null);
  React.useEffect(() => {
    // Drop any stale error when the dialog reopens for a new sentiment.
    setSubmitError(null);
  }, [sentiment]);

  const availableCategories = categoriesForSentiment(sentiment);
  const needsNotes = selectedCategories.has(FeedbackCategory.OTHER);
  const canSubmit = !(needsNotes && notes.trim().length === 0);

  const toggleCategory = (category: FeedbackCategory) => {
    setSelectedCategories((prev) => {
      const next = new Set(prev);
      if (next.has(category)) {
        next.delete(category);
      } else {
        next.add(category);
      }
      return next;
    });
  };

  const eventContext = { threadId, messageSequenceNum };

  const handleSubmit = () => {
    if (sentiment === null || !canSubmit) {
      return;
    }
    setSubmitError(null);
    submitFeedback.mutate(
      {
        threadId,
        agentThreadOrgId,
        messageSequenceNum,
        sentiment,
        categories: Array.from(selectedCategories),
        notes: notes.trim() || null,
      },
      {
        onSuccess: onClose,
        onError: (error) => {
          ErrorMonitoringService.captureError(error, {
            context: { threadId, messageSequenceNum, sentiment },
          });
          // Leave the dialog open so the user's input survives the failure.
          setSubmitError(
            error instanceof Error
              ? `Couldn't submit feedback: ${error.message}`
              : "Couldn't submit feedback. Please try again.",
          );
        },
      },
    );
  };

  return (
    <Dialog open={sentiment !== null} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        {sentiment === FeedbackSentiment.POSITIVE
          ? "What was good about this response?"
          : "What could be improved?"}
      </DialogTitle>
      <DialogContent dividers>
        <Box className={styles.chipGroup}>
          {availableCategories.map((category) => {
            const selected = selectedCategories.has(category);
            return (
              <Chip
                key={category}
                label={FEEDBACK_CATEGORY_LABELS[category]}
                clickable
                onClick={() => toggleCategory(category)}
                color={selected ? "primary" : "default"}
                variant={selected ? "filled" : "outlined"}
                size="small"
              />
            );
          })}
        </Box>
        <TextField
          placeholder={
            needsNotes
              ? "Describe what happened (required)"
              : "Add a note (optional)"
          }
          multiline
          rows={3}
          fullWidth
          value={notes}
          onChange={(e) => setNotes(e.target.value)}
          className={styles.feedbackInput}
          margin="normal"
          size="small"
        />
        <RobotoTypography
          variant="caption"
          sx={{ color: theme.palette.text.secondary }}
        >
          Please don&apos;t paste sensitive customer data.
        </RobotoTypography>
        {submitError !== null && (
          <RobotoTypography
            variant="caption"
            sx={{ color: theme.palette.error.main, display: "block", mt: 1 }}
          >
            {submitError}
          </RobotoTypography>
        )}
      </DialogContent>
      <DialogActions>
        <RobotoButton
          eventName="AgentThreadFeedbackCancelClicked"
          eventProperties={eventContext}
          variant="text"
          onClick={onClose}
        >
          Cancel
        </RobotoButton>
        <RobotoButton
          eventName="AgentThreadFeedbackSubmitClicked"
          eventProperties={{
            ...eventContext,
            sentiment: sentimentForAnalytics(sentiment),
            // All selected categories, not just the first — the previous
            // shape silently dropped every category after [0].
            categories: Array.from(selectedCategories).map((c) => c.toString()),
          }}
          variant="contained"
          onClick={handleSubmit}
          disabled={!canSubmit || submitFeedback.isPending}
        >
          Submit
        </RobotoButton>
      </DialogActions>
    </Dialog>
  );
};
